import React, { useState } from 'react';
import Api from "../Api/Api.json";
import { Snackbar, Alert } from "@mui/material";

const UploadVideo = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [topic, setTopic] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const base_url = Api.MAGICVERSE_USER_API;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file is a video
      if (!file.type.startsWith('video/')) {
        setSnackbar({
          open: true,
          message: "Please select a valid video file",
          severity: "error",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Please select a video file first",
        severity: "error",
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Get auth token
      const auth_user = await localStorage.getItem("auth_user");
      const id_token = JSON.parse(auth_user)?.id_token;

      if (!id_token) {
        throw new Error("Authentication token not found");
      }

      // Step 1: Get presigned URL
      const presignedResponse = await fetch(base_url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task: "getUserVideoUploadPresignedURL",
          topic: topic || "",
          filename: selectedFile.name,
          content_type: selectedFile.type
        })
      });

      if (!presignedResponse.ok) {
        throw new Error(`Failed to get presigned URL: ${presignedResponse.status}`);
      }
      const resp=await presignedResponse.json();
      //console.log(resp);

      const [ presigned_url, filename, s3_file_key ] = resp;

      
      
      const uploadResponse = await fetch(presigned_url, {
        method: "PUT",
        headers: {
          "Content-Type": selectedFile.type, // important for S3 to store correct mime-type
        },
        body: selectedFile
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('Upload response status:', uploadResponse.status);
        console.error('Upload response headers:', uploadResponse.headers);
        console.error('Upload response text:', errorText);
        throw new Error(`Failed to upload file: ${uploadResponse.status} - ${errorText}`);
      }

      setUploadProgress(50);

      // Step 3: Post upload completion
      const req_dict={
        task: "postUserVideoUpload",
        filename: filename,
        s3_file_key: s3_file_key
      }
      console.log(req_dict);
      const postUploadResponse = await fetch(base_url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(req_dict)
      });

      const resps=await postUploadResponse.json();
      //console.log(resps);

      if (!postUploadResponse.ok) {
        throw new Error(`Failed to post upload: ${postUploadResponse.status}`);
      }

      setUploadProgress(100);

      setSnackbar({
        open: true,
        message: "Video uploaded successfully!",
        severity: "success",
      });

      // Reset form
      setSelectedFile(null);
      setTopic('');
      setUploadProgress(0);

    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({
        open: true,
        message: `Upload failed: ${error.message}`,
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '24px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          color: '#333', 
          fontSize: '24px',
          textAlign: 'center'
        }}>
          Upload Video
        </h2>

        {/* Topic Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '16px', 
            color: '#555',
            fontWeight: '500'
          }}>
            Topic (Optional):
          </label>
          <input 
            type="text" 
            value={topic} 
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter video topic..."
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: '1px solid #ccc', 
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* File Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '16px', 
            color: '#555',
            fontWeight: '500'
          }}>
            Select Video File:
          </label>
          <input 
            type="file" 
            accept="video/*" 
            onChange={handleFileSelect}
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: '1px solid #ccc', 
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          {selectedFile && (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px 12px',
              backgroundColor: '#e8f5e8',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#2d5a2d'
            }}>
              Selected: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
            </div>
          )}
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '8px',
              fontSize: '14px',
              color: '#666'
            }}>
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: '#4CAF50',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}

        {/* Upload Button */}
        <button 
          onClick={handleUpload}
          disabled={isUploading || !selectedFile}
          style={{
            width: '100%',
            padding: '14px 20px',
            backgroundColor: isUploading || !selectedFile ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isUploading || !selectedFile ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background-color 0.3s ease'
          }}
        >
          {isUploading ? 'Uploading...' : 'Upload Video'}
        </button>

        {/* Instructions */}
        <div style={{ 
          marginTop: '24px', 
          padding: '16px',
          backgroundColor: '#f0f8ff',
          borderRadius: '6px',
          border: '1px solid #d1e7ff'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#2c5aa0' }}>Instructions:</h4>
          <ul style={{ margin: '0', paddingLeft: '20px', color: '#555', fontSize: '14px' }}>
            <li>Select a video file (MP4, AVI, MOV, etc.)</li>
            <li>Optionally add a topic description</li>
            <li>Click "Upload Video" to start the upload process</li>
            <li>The upload will be processed in the background</li>
          </ul>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UploadVideo;

